import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Edit, Save, X, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { StockItemDrawerDetails } from "@/components/stock-item-drawer-details";
import { StockItem } from "@/types/stock-item";
import { useNavigation } from "@/components/layout/app-layout";
import { useScreenMode } from "@/providers/ModeProvider";
import { useStockItems } from "@/hooks/use-stock-items";
import { getTransactionCount } from "@/data/mockTransactions";
import { mockStockItems } from "@/data/mockStockItems";

interface StockItemDetailPageProps {
  pageParams?: {
    itemCode?: string;
  };
}

// Use centralized mock stock items with multi-location support
// (imported from @/data/mockStockItems)

// Mock function to get stock item by code
const getStockItemByCode = (code: string): StockItem | null => {
  return mockStockItems.find((item) => item.code === code) || null;
};

export function StockItemDetailPage({ pageParams }: StockItemDetailPageProps) {
  const { navigateTo } = useNavigation();
  
  // Mode context for demo/live toggle (screen-specific)
  const { setMode, isDemo, isLive } = useScreenMode("stock-item-detail");
  const isLiveMode = isLive;
  
  // Backend data fetching hook
  const {
    isLoading: isBackendLoading,
    fetchStockItemByCode,
    updateStockItem: updateStockItemBackend,
  } = useStockItems();
  
  const [stockItem, setStockItem] = useState<StockItem | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalItem, setOriginalItem] = useState<StockItem | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
    },
    [setMode],
  );

  // Load stock item on mount or when mode changes
  useEffect(() => {
    const loadStockItem = async () => {
      if (!pageParams?.itemCode) return;
      
      if (isLiveMode) {
        // Fetch from backend in live mode
        const item = await fetchStockItemByCode(pageParams.itemCode);
        if (item) {
          setStockItem(item);
          setOriginalItem(JSON.parse(JSON.stringify(item)));
        } else {
          setStockItem(null);
          setOriginalItem(null);
        }
      } else {
        // Use mock data in demo mode
        const item = getStockItemByCode(pageParams.itemCode);
        if (item) {
          setStockItem(item);
          setOriginalItem(JSON.parse(JSON.stringify(item)));
        } else {
          setStockItem(null);
          setOriginalItem(null);
        }
      }
    };
    
    loadStockItem();
  }, [pageParams?.itemCode, isLiveMode, fetchStockItemByCode]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (confirm("You have unsaved changes. Are you sure you want to leave?")) {
        navigateTo("stock-items");
      }
    } else {
      navigateTo("stock-items");
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      if (confirm("Discard unsaved changes?")) {
        setStockItem(originalItem ? JSON.parse(JSON.stringify(originalItem)) : null);
        setIsEditMode(false);
        setHasUnsavedChanges(false);
      }
    } else {
      setIsEditMode(false);
    }
  };

  const handleSave = async () => {
    if (!stockItem) return;

    setIsSaving(true);
    try {
      if (isLiveMode) {
        // Use backend API in live mode
        const success = await updateStockItemBackend(stockItem);
        if (!success) {
          throw new Error("Failed to update stock item");
        }
      } else {
        // Simulate API call in demo mode
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      setOriginalItem(JSON.parse(JSON.stringify(stockItem)));
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      toast.success("Stock item updated successfully");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleItemChange = (updatedItem: StockItem) => {
    setStockItem(updatedItem);
    setHasUnsavedChanges(true);
  };

  const handleNavigateToTransaction = (type: string, reference: string, transactionId: string) => {
    // Map transaction types to page routes
    const typeToPageMap: Record<string, string> = {
      "purchase-order": "purchase-orders",
      "sales-order": "sales-orders",
      "stock-adjustment": "stock-movements",
      "stock-transfer": "stock-movements",
      "customer-return": "stock-movements",
      "supplier-return": "stock-movements",
    };

    const targetPage = typeToPageMap[type];
    if (targetPage) {
      // Navigate to the target page with the reference number (not the transaction ID)
      navigateTo(targetPage, { transactionId: reference });
      toast.info(`Navigating to ${reference}...`);
    } else {
      toast.error("Unknown transaction type");
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  if (!pageParams?.itemCode) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <Package className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-semibold mb-2">No Stock Item Selected</h2>
        <p className="text-muted-foreground mb-4">Please select a stock item to view details.</p>
        <Button onClick={() => navigateTo("stock-items")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Stock Items
        </Button>
      </div>
    );
  }

  if (!stockItem) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <Package className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Stock Item Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The stock item with code "{pageParams.itemCode}" could not be found.
        </p>
        <Button onClick={() => navigateTo("stock-items")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Stock Items
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-background p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div>
              <h1 className="text-2xl font-semibold">{stockItem.code}</h1>
              <p className="text-sm text-muted-foreground">{stockItem.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Demo/Live Mode Toggle */}
            {isBackendLoading ? (
              <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Loading...
              </Badge>
            ) : isDemo ? (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Demo Mode
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Live Mode
              </Badge>
            )}
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDemo ? "text-muted-foreground" : "font-medium"}`}>Demo</span>
              <Switch
                checked={isLive}
                onCheckedChange={(checked) => handleModeToggle(checked)}
                disabled={isBackendLoading}
              />
              <span className={`text-sm ${isLive ? "font-medium" : "text-muted-foreground"}`}>Live</span>
            </div>
            <Separator orientation="vertical" className="h-6" />
            {stockItem.inactive ? (
              <Badge variant="secondary" className="bg-muted text-muted-foreground">
                Inactive
              </Badge>
            ) : (
              <Badge variant="success" accent="success">
                Active
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-xs text-muted-foreground">Physical Quantity</p>
              <p className="text-lg font-semibold">{stockItem.physicalQuantity}</p>
            </div>
            <Separator orientation="vertical" className="h-10" />
            <div>
              <p className="text-xs text-muted-foreground">Unit Price</p>
              <p className="text-lg font-semibold">{formatCurrency(stockItem.unitPrice)}</p>
            </div>
            <Separator orientation="vertical" className="h-10" />
            <div>
              <p className="text-xs text-muted-foreground">Total Value</p>
              <p className="text-lg font-semibold">{formatCurrency(stockItem.value)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving || !hasUnsavedChanges}>
                  {isSaving ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <Card className="p-6">
          <StockItemDrawerDetails
            item={stockItem}
            isEditMode={isEditMode}
            onItemChange={handleItemChange}
            isFullViewMode={true}
            isLiveMode={isLiveMode}
            onNavigateToTransaction={handleNavigateToTransaction}
          />
        </Card>
      </div>
    </div>
  );
}
